/**
 * Builds the v2-pipeline plan for `unassign`. `shouldIncludeMaster` defaults
 * to `true`; an explicit `false` opts into the daily-only "Save current day"
 * shape. Past-date intents skip master + future cascade (BE cascade no-ops).
 * The defensive `UNASSIGN_FUTURE_DAILY` step catches future occurrences the
 * BE master cascade can't reach — `v1-bulk` skips it.
 */
import { type CseId, type WorkOrderId } from 'types/entities/shared/ids'
import { isDailyStop, isOnRequestStop } from 'types/modules/routing'

import { dayOfWeekForServiceDate } from '../dayOfWeek'
import { getDailyOpsScope } from '../decisions'
import { makeCascadeFutureDailyStep } from '../steps/cascadeFutureDaily'
import { makePreflightStep } from '../steps/preflight'
import { makeUnassignDailyStep } from '../steps/unassignDaily'
import { makeUnassignMasterStep } from '../steps/unassignMaster'
import { makeVerifyStep } from '../steps/verify'

import type { Deps, OperationPlan, PipelineStep, UnassignIntent } from 'types/modules/routing'

const STRATEGY_ID = 'v2-pipeline' as const

const collectIds = (
  intent: UnassignIntent
): {
  readonly workOrderIds: ReadonlyArray<WorkOrderId>
  readonly cseIds: ReadonlyArray<CseId>
} => {
  const workOrderIds: WorkOrderId[] = []
  const cseIds: CseId[] = []
  const seenWo = new Set<string>()
  const seenCse = new Set<string>()
  for (const stop of intent.stops) {
    if (isDailyStop(stop) || isOnRequestStop(stop)) {
      const wo = stop.workOrderId
      if (wo && !seenWo.has(wo)) {
        seenWo.add(wo)
        workOrderIds.push(wo)
      }
      if (isDailyStop(stop) && stop.configuredServiceEventId) {
        const cse = stop.configuredServiceEventId
        if (!seenCse.has(cse)) {
          seenCse.add(cse)
          cseIds.push(cse)
        }
      }
    }
  }
  return { workOrderIds, cseIds }
}

export const buildUnassignPipeline = (intent: UnassignIntent, deps: Deps): OperationPlan => {
  const steps: PipelineStep[] = []
  const { workOrderIds, cseIds } = collectIds(intent)

  steps.push(makePreflightStep({ intent }, deps) as PipelineStep)

  if (workOrderIds.length > 0) {
    steps.push(makeUnassignDailyStep({ routeId: intent.routeId, workOrderIds }, deps) as PipelineStep)
  }

  const scope = getDailyOpsScope(intent)
  if (scope.runMasterFirst && cseIds.length > 0) {
    const dayOfWeek = dayOfWeekForServiceDate(intent.serviceDate)
    if (dayOfWeek) {
      steps.push(
        makeUnassignMasterStep(
          {
            routeId: intent.routeId,
            dayOfWeek,
            cseIds,
            futureStops: true,
          },
          deps
        ) as PipelineStep
      )
    }
  }

  if (scope.runFutureCascade && workOrderIds.length > 0) {
    steps.push(
      makeCascadeFutureDailyStep(
        {
          mode: 'unassign',
          phase: 'UNASSIGN_FUTURE_DAILY',
          routeId: intent.routeId,
          currentServiceDate: intent.serviceDate,
          workOrderIds,
        },
        deps
      ) as PipelineStep
    )
  }

  steps.push(
    makeVerifyStep(
      {
        intent,
        expectedUnassignedWorkOrderIds: workOrderIds.map(String),
      },
      deps
    ) as PipelineStep
  )

  return {
    strategy: STRATEGY_ID,
    intent,
    steps,
  }
}
